/**
 * Aggregate P&L View Component
 *
 * Displays P&L data aggregated across all clinics the user has access to.
 * Features per-brand breakdown charts, summary cards, and trend visualization.
 */

import { useMemo, useState, useRef, useEffect } from 'react'
import { Card, CardHeader, CardTitle, CardContent, Button } from './ui'
import { BRAND_COLORS, getBrandColor } from '../lib/brandColors'
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts'
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Package,
  ShoppingCart,
  AlertTriangle,
  Loader2,
  Users,
  RefreshCw,
  Filter,
  Check,
  ChevronDown
} from 'lucide-react'

/**
 * Format cents to dollars display
 */
function formatCurrency(cents, compact = false) {
  const dollars = cents / 100
  if (compact && Math.abs(dollars) >= 1000) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      notation: 'compact',
      maximumFractionDigits: 1
    }).format(dollars)
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(dollars)
}

/**
 * Format number with commas
 */
function formatNumber(num) {
  return new Intl.NumberFormat('en-US').format(num)
}

/**
 * Delta Badge - shows prior-period percentage change
 */
function DeltaBadge({ current, prior, invert = false }) {
  if (!prior || prior === 0) return null
  const pct = ((current - prior) / prior) * 100
  if (!isFinite(pct)) return null
  const isPositive = pct > 0
  const isGood = invert ? !isPositive : isPositive
  return (
    <span className={`inline-flex items-center text-xs font-medium ${isGood ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}>
      {isPositive ? '↑' : '↓'} {Math.abs(pct).toFixed(1)}%
    </span>
  )
}

/**
 * Summary Card Component
 */
function SummaryCard({ title, value, subtitle, icon: Icon, delta, loading, children }) {
  return (
    <Card className="relative overflow-hidden">
      <CardContent className="p-6">
        {loading ? (
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-muted rounded w-1/2"></div>
            <div className="h-8 bg-muted rounded w-3/4"></div>
            <div className="h-3 bg-muted rounded w-1/3"></div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-muted-foreground">{title}</span>
              {Icon && (
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Icon className="h-4 w-4 text-primary" />
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold tracking-tight">{value}</span>
              {delta}
            </div>
            {subtitle && (
              <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
            )}
            {children}
          </>
        )}
      </CardContent>
    </Card>
  )
}

/**
 * Custom Tooltip for Charts
 */
function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null

  return (
    <div className="bg-popover border border-border rounded-lg shadow-lg p-3 min-w-[180px]">
      <p className="font-medium text-sm mb-2">{label}</p>
      <div className="space-y-1">
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center justify-between text-xs">
            <span className="flex items-center gap-1.5">
              <span
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: entry.color }}
              />
              {entry.name}
            </span>
            <span className="font-medium">
              {entry.name.toLowerCase().includes('orders')
                ? formatNumber(entry.value)
                : formatCurrency(entry.value)}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * Pie Chart Tooltip
 */
function PieTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null

  const data = payload[0]
  return (
    <div className="bg-popover border border-border rounded-lg shadow-lg p-3">
      <p className="font-medium text-sm flex items-center gap-2">
        <span
          className="w-3 h-3 rounded-full"
          style={{ backgroundColor: data.payload.color }}
        />
        {data.name}
      </p>
      <p className="text-lg font-bold mt-1">{formatCurrency(data.value)}</p>
      <p className="text-xs text-muted-foreground">{data.payload.percent}% of total</p>
    </div>
  )
}

/**
 * Brand Filter Dropdown
 */
function BrandFilter({ brands, selectedBrands, onSelectionChange }) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const allSelected = selectedBrands.length === brands.length
  const noneSelected = selectedBrands.length === 0

  const toggleBrand = (clinicId) => {
    if (selectedBrands.includes(clinicId)) {
      // Don't allow deselecting the last brand
      if (selectedBrands.length > 1) {
        onSelectionChange(selectedBrands.filter(id => id !== clinicId))
      }
    } else {
      onSelectionChange([...selectedBrands, clinicId])
    }
  }

  const selectAll = () => {
    onSelectionChange(brands.map(b => b.clinicId))
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className="h-8 gap-2"
      >
        <Filter className="h-4 w-4" />
        <span className="hidden sm:inline">
          {allSelected ? 'All Brands' : `${selectedBrands.length} Brand${selectedBrands.length !== 1 ? 's' : ''}`}
        </span>
        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </Button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-popover border border-border rounded-lg shadow-lg z-50">
          <div className="p-2">
            <div className="text-xs font-medium text-muted-foreground px-2 py-1 mb-1">
              Filter Brands
            </div>

            {/* Select All option */}
            <button
              onClick={selectAll}
              className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-muted text-left"
            >
              <div className={`w-4 h-4 rounded border flex items-center justify-center ${allSelected ? 'bg-primary border-primary' : 'border-muted-foreground/30'}`}>
                {allSelected && <Check className="h-3 w-3 text-primary-foreground" />}
              </div>
              <span className="font-medium">Select All</span>
            </button>

            <div className="border-t border-border my-1" />

            {/* Individual brand options */}
            {brands.map((brand) => {
              const isSelected = selectedBrands.includes(brand.clinicId)
              const isLastSelected = isSelected && selectedBrands.length === 1

              return (
                <button
                  key={brand.clinicId}
                  onClick={() => toggleBrand(brand.clinicId)}
                  disabled={isLastSelected}
                  className={`w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-muted text-left ${isLastSelected ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className={`w-4 h-4 rounded border flex items-center justify-center ${isSelected ? 'bg-primary border-primary' : 'border-muted-foreground/30'}`}>
                    {isSelected && <Check className="h-3 w-3 text-primary-foreground" />}
                  </div>
                  <span
                    className="w-3 h-3 rounded-full shrink-0"
                    style={{ backgroundColor: brand.color || getBrandColor(brand.clinicId) }}
                  />
                  <span>{brand.displayName}</span>
                </button>
              )
            })}

            {selectedBrands.length === 1 && (
              <p className="text-xs text-muted-foreground px-2 py-1 mt-1">
                At least one brand must be selected
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default function AggregatePnLView({ data, loading, errors, onBrandFilterChange }) {
  // Track which brands are selected (default: all)
  const [selectedBrands, setSelectedBrands] = useState([])

  // Initialize selected brands when data loads
  useEffect(() => {
    if (data?.brand_breakdown && selectedBrands.length === 0) {
      setSelectedBrands(data.brand_breakdown.map(b => b.clinicId))
    }
  }, [data?.brand_breakdown])

  // Filter data based on selected brands
  const filteredData = useMemo(() => {
    if (!data || selectedBrands.length === 0) return data
    if (selectedBrands.length === data.brand_breakdown?.length) return data

    const filteredBreakdown = data.brand_breakdown.filter(b => selectedBrands.includes(b.clinicId))

    // Recalculate summary
    const summary = filteredBreakdown.reduce((acc, brand) => ({
      total_revenue: acc.total_revenue + brand.summary.total_revenue,
      total_expenses: acc.total_expenses + brand.summary.total_expenses,
      gross_profit: acc.gross_profit + brand.summary.gross_profit,
      total_orders: acc.total_orders + brand.summary.total_orders,
      new_orders: acc.new_orders + brand.summary.new_orders,
      refill_orders: acc.refill_orders + brand.summary.refill_orders
    }), {
      total_revenue: 0,
      total_expenses: 0,
      gross_profit: 0,
      total_orders: 0,
      new_orders: 0,
      refill_orders: 0
    })

    summary.margin_percent = summary.total_revenue > 0
      ? parseFloat(((summary.gross_profit / summary.total_revenue) * 100).toFixed(1))
      : 0

    // Recalculate expenses
    const expenses = filteredBreakdown.reduce((acc, brand) => ({
      cogs: acc.cogs + (brand.expenses?.cogs || 0),
      shipping: acc.shipping + (brand.expenses?.shipping || 0),
      ad_spend: acc.ad_spend + (brand.expenses?.ad_spend || 0),
      other: acc.other + (brand.expenses?.other || 0)
    }), { cogs: 0, shipping: 0, ad_spend: 0, other: 0 })

    // Recalculate daily with filtered brands
    const daily = data.daily.map(day => {
      const filteredByBrand = {}
      let totalRevenue = 0
      let totalExpenses = 0
      let profit = 0
      let totalOrders = 0

      Object.entries(day.by_brand || {}).forEach(([brandId, brandData]) => {
        if (selectedBrands.includes(brandId)) {
          filteredByBrand[brandId] = brandData
          totalRevenue += brandData.revenue || 0
          totalExpenses += brandData.expenses || 0
          profit += brandData.profit || 0
          totalOrders += brandData.orders || 0
        }
      })

      return {
        ...day,
        total_revenue: totalRevenue,
        total_expenses: totalExpenses,
        profit: profit,
        total_orders: totalOrders,
        by_brand: filteredByBrand
      }
    })

    // Recalculate prior_summary so the comparison delta reflects the same brand subset
    let prior_summary = data.prior_summary
    if (Array.isArray(data.prior_brand_breakdown)) {
      const filteredPrior = data.prior_brand_breakdown.filter(b => selectedBrands.includes(b.clinicId))
      const ps = filteredPrior.reduce((acc, brand) => ({
        total_revenue: acc.total_revenue + (brand.summary?.total_revenue || 0),
        total_expenses: acc.total_expenses + (brand.summary?.total_expenses || 0),
        gross_profit: acc.gross_profit + (brand.summary?.gross_profit || 0),
        total_orders: acc.total_orders + (brand.summary?.total_orders || 0),
        new_orders: acc.new_orders + (brand.summary?.new_orders || 0),
        refill_orders: acc.refill_orders + (brand.summary?.refill_orders || 0),
      }), { total_revenue: 0, total_expenses: 0, gross_profit: 0, total_orders: 0, new_orders: 0, refill_orders: 0 })
      ps.margin_percent = ps.total_revenue > 0
        ? parseFloat(((ps.gross_profit / ps.total_revenue) * 100).toFixed(1))
        : 0
      prior_summary = ps
    }

    return {
      ...data,
      summary,
      expenses,
      brand_breakdown: filteredBreakdown,
      daily,
      clinics_included: selectedBrands,
      prior_summary,
    }
  }, [data, selectedBrands])
  // Prepare brand revenue data for pie chart
  const brandRevenueData = useMemo(() => {
    if (!filteredData?.brand_breakdown) return []

    const totalRevenue = filteredData.summary?.total_revenue || 1
    return filteredData.brand_breakdown.map(brand => ({
      name: brand.displayName,
      value: brand.summary.total_revenue,
      color: brand.color || getBrandColor(brand.clinicId),
      percent: ((brand.summary.total_revenue / totalRevenue) * 100).toFixed(1)
    }))
  }, [filteredData])

  // Prepare brand profit data for bar chart
  const brandProfitData = useMemo(() => {
    if (!filteredData?.brand_breakdown) return []

    return filteredData.brand_breakdown.map(brand => ({
      name: brand.displayName,
      profit: brand.summary.gross_profit,
      revenue: brand.summary.total_revenue,
      color: brand.color || getBrandColor(brand.clinicId)
    }))
  }, [filteredData])

  // Prepare order breakdown data (new vs refill by brand)
  const orderBreakdownData = useMemo(() => {
    if (!filteredData?.brand_breakdown) return []

    return filteredData.brand_breakdown.map(brand => ({
      name: brand.displayName,
      newOrders: brand.summary.new_orders || 0,
      refillOrders: brand.summary.refill_orders || 0,
      total: brand.summary.total_orders || 0,
      color: brand.color || getBrandColor(brand.clinicId)
    }))
  }, [filteredData])

  // Prepare stacked trend data (revenue)
  const trendData = useMemo(() => {
    if (!filteredData?.daily) return []

    return filteredData.daily.map(day => {
      const point = {
        date: day.displayDate,
        total: day.total_revenue
      }
      // Add per-brand revenue
      Object.entries(day.by_brand || {}).forEach(([brandId, brandData]) => {
        point[brandId] = brandData.revenue
      })
      return point
    })
  }, [filteredData])

  // Prepare daily orders by brand data
  const dailyOrdersData = useMemo(() => {
    if (!filteredData?.daily) return []

    return filteredData.daily.map(day => {
      const point = {
        date: day.displayDate,
        total: day.total_orders
      }
      // Add per-brand orders
      Object.entries(day.by_brand || {}).forEach(([brandId, brandData]) => {
        point[brandId] = brandData.orders || 0
      })
      return point
    })
  }, [filteredData])

  // Get brand IDs for stacked chart
  const brandIds = useMemo(() => {
    if (!filteredData?.brand_breakdown) return []
    return filteredData.brand_breakdown.map(b => b.clinicId)
  }, [filteredData])

  // Calculate order percentages
  const orderStats = useMemo(() => {
    const total = filteredData?.summary?.total_orders || 0
    const newOrders = filteredData?.summary?.new_orders || 0
    const refillOrders = filteredData?.summary?.refill_orders || 0
    return {
      total,
      newOrders,
      refillOrders,
      newPercent: total > 0 ? ((newOrders / total) * 100).toFixed(1) : 0,
      refillPercent: total > 0 ? ((refillOrders / total) * 100).toFixed(1) : 0
    }
  }, [filteredData])

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Loading Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <SummaryCard key={i} title="" value="" loading={true} />
          ))}
        </div>
        <Card className="p-8">
          <div className="flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <span className="ml-3 text-muted-foreground">Loading aggregate data...</span>
          </div>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Error Alert */}
      {errors && errors.length > 0 && (
        <Card className="border-yellow-500/50 bg-yellow-500/10">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-yellow-800 dark:text-yellow-200">
                  Some clinics failed to load
                </p>
                <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                  {errors.map(e => e.clinicId).join(', ')} - data may be incomplete
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Brand Filter */}
      {data?.brand_breakdown && data.brand_breakdown.length > 1 && (
        <div className="flex justify-end">
          <BrandFilter
            brands={data.brand_breakdown}
            selectedBrands={selectedBrands}
            onSelectionChange={setSelectedBrands}
          />
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard
          title="Total Revenue"
          value={formatCurrency(filteredData?.summary?.total_revenue || 0)}
          subtitle={`Across ${filteredData?.clinics_included?.length || 0} brand${filteredData?.clinics_included?.length !== 1 ? 's' : ''}`}
          icon={DollarSign}
          delta={<DeltaBadge current={filteredData?.summary?.total_revenue} prior={filteredData?.prior_summary?.total_revenue} />}
          loading={loading}
        />
        <SummaryCard
          title="Total Expenses"
          value={formatCurrency(filteredData?.summary?.total_expenses || 0)}
          subtitle="COGS + Shipping + Ads"
          icon={Package}
          delta={<DeltaBadge current={filteredData?.summary?.total_expenses} prior={filteredData?.prior_summary?.total_expenses} invert />}
          loading={loading}
        />
        <SummaryCard
          title="Net Revenue"
          value={formatCurrency(filteredData?.summary?.gross_profit || 0)}
          subtitle={`${filteredData?.summary?.margin_percent || 0}% margin`}
          icon={filteredData?.summary?.gross_profit >= 0 ? TrendingUp : TrendingDown}
          delta={<DeltaBadge current={filteredData?.summary?.gross_profit} prior={filteredData?.prior_summary?.gross_profit} />}
          loading={loading}
        />
        <SummaryCard
          title="Total Orders"
          value={formatNumber(filteredData?.summary?.total_orders || 0)}
          icon={ShoppingCart}
          delta={<DeltaBadge current={filteredData?.summary?.total_orders} prior={filteredData?.prior_summary?.total_orders} />}
          loading={loading}
        >
          {/* Order breakdown bar */}
          <div className="mt-3">
            <div className="flex h-2 rounded-full overflow-hidden bg-muted">
              <div
                className="bg-primary"
                style={{ width: `${orderStats.newPercent}%` }}
                title={`New: ${orderStats.newOrders}`}
              />
              <div
                className="bg-primary/40"
                style={{ width: `${orderStats.refillPercent}%` }}
                title={`Refill: ${orderStats.refillOrders}`}
              />
            </div>
            <div className="flex justify-between mt-1 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Users className="h-3 w-3" />
                {formatNumber(orderStats.newOrders)} new ({orderStats.newPercent}%)
              </span>
              <span className="flex items-center gap-1">
                <RefreshCw className="h-3 w-3" />
                {formatNumber(orderStats.refillOrders)} refill ({orderStats.refillPercent}%)
              </span>
            </div>
          </div>
        </SummaryCard>
      </div>

      {/* Order Breakdown + Expense Breakdown Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* New vs Refill Orders by Brand */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium">Orders by Brand (New vs Refill)</CardTitle>
          </CardHeader>
          <CardContent>
            {orderBreakdownData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={orderBreakdownData} layout="vertical" margin={{ left: 20, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" horizontal={true} vertical={false} />
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={80} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="newOrders" name="New Orders" stackId="orders" fill="hsl(var(--primary))" />
                  <Bar dataKey="refillOrders" name="Refill Orders" stackId="orders" fill="hsl(var(--primary) / 0.4)" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-muted-foreground">
                No data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Aggregate Expense Breakdown */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium">Expense Breakdown{selectedBrands.length < (data?.brand_breakdown?.length || 0) ? ' (Filtered)' : ' (All Brands)'}</CardTitle>
          </CardHeader>
          <CardContent>
            {filteredData?.expenses ? (
              <div className="space-y-4">
                {/* Expense items */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">COGS (Drug Costs)</span>
                    <span className="font-medium">{formatCurrency(filteredData.expenses.cogs || 0)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Shipping ($17/order)</span>
                    <span className="font-medium">{formatCurrency(filteredData.expenses.shipping || 0)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Ad Spend</span>
                    <span className={`font-medium ${filteredData.expenses.ad_spend ? '' : 'text-muted-foreground italic'}`}>
                      {filteredData.expenses.ad_spend ? formatCurrency(filteredData.expenses.ad_spend) : 'N/A'}
                    </span>
                  </div>
                  {filteredData.expenses.other > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Other</span>
                      <span className="font-medium">{formatCurrency(filteredData.expenses.other)}</span>
                    </div>
                  )}
                  <div className="border-t pt-3 flex items-center justify-between">
                    <span className="font-medium">Total Expenses</span>
                    <span className="font-bold text-lg">{formatCurrency(filteredData.summary?.total_expenses || 0)}</span>
                  </div>
                </div>

                {/* Visual breakdown bar */}
                <div>
                  <div className="flex h-4 rounded-full overflow-hidden bg-muted">
                    {filteredData.expenses.cogs > 0 && (
                      <div
                        className="bg-orange-500"
                        style={{ width: `${(filteredData.expenses.cogs / filteredData.summary.total_expenses) * 100}%` }}
                        title={`COGS: ${formatCurrency(filteredData.expenses.cogs)}`}
                      />
                    )}
                    {filteredData.expenses.shipping > 0 && (
                      <div
                        className="bg-blue-500"
                        style={{ width: `${(filteredData.expenses.shipping / filteredData.summary.total_expenses) * 100}%` }}
                        title={`Shipping: ${formatCurrency(filteredData.expenses.shipping)}`}
                      />
                    )}
                    {filteredData.expenses.ad_spend > 0 && (
                      <div
                        className="bg-purple-500"
                        style={{ width: `${(filteredData.expenses.ad_spend / filteredData.summary.total_expenses) * 100}%` }}
                        title={`Ads: ${formatCurrency(filteredData.expenses.ad_spend)}`}
                      />
                    )}
                  </div>
                  <div className="flex gap-4 mt-2 text-xs">
                    <span className="flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-orange-500" /> COGS
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-blue-500" /> Shipping
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-purple-500" /> Ads
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-muted-foreground">
                No expense data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Brand Breakdown Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Revenue by Brand Pie Chart */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium">Revenue by Brand</CardTitle>
          </CardHeader>
          <CardContent>
            {brandRevenueData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={brandRevenueData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    nameKey="name"
                    label={({ name, percent }) => `${name} (${percent}%)`}
                    labelLine={false}
                  >
                    {brandRevenueData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<PieTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-muted-foreground">
                No data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Profit by Brand Bar Chart */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium">Net Revenue by Brand</CardTitle>
          </CardHeader>
          <CardContent>
            {brandProfitData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart
                  data={brandProfitData}
                  layout="vertical"
                  margin={{ left: 20, right: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" horizontal={true} vertical={false} />
                  <XAxis
                    type="number"
                    tickFormatter={(value) => formatCurrency(value, true)}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    tick={{ fontSize: 12 }}
                    width={80}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar
                    dataKey="profit"
                    name="Net Revenue"
                    radius={[0, 4, 4, 0]}
                  >
                    {brandProfitData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-muted-foreground">
                No data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Revenue Trend - Stacked Area Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-medium">Revenue Trend by Brand</CardTitle>
        </CardHeader>
        <CardContent>
          {trendData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                  interval={Math.floor(trendData.length / 8)}
                />
                <YAxis
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(value) => formatCurrency(value, true)}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                {brandIds.map((brandId, index) => {
                  const brand = filteredData?.brand_breakdown?.find(b => b.clinicId === brandId)
                  return (
                    <Area
                      key={brandId}
                      type="monotone"
                      dataKey={brandId}
                      name={brand?.displayName || brandId}
                      stackId="1"
                      fill={brand?.color || getBrandColor(brandId)}
                      stroke={brand?.color || getBrandColor(brandId)}
                      fillOpacity={0.6}
                    />
                  )
                })}
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-muted-foreground">
              No data available for selected period
            </div>
          )}
        </CardContent>
      </Card>

      {/* Daily Orders by Brand - Stacked Bar Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-medium">Daily Orders by Brand</CardTitle>
        </CardHeader>
        <CardContent>
          {dailyOrdersData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={dailyOrdersData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                  interval={Math.floor(dailyOrdersData.length / 8)}
                />
                <YAxis
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                {brandIds.map((brandId, index) => {
                  const brand = filteredData?.brand_breakdown?.find(b => b.clinicId === brandId)
                  return (
                    <Bar
                      key={brandId}
                      dataKey={brandId}
                      name={`${brand?.displayName || brandId} Orders`}
                      stackId="orders"
                      fill={brand?.color || getBrandColor(brandId)}
                      radius={index === brandIds.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                    />
                  )
                })}
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-muted-foreground">
              No data available for selected period
            </div>
          )}
        </CardContent>
      </Card>

      {/* Brand Summary Table - Enhanced with expenses */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-medium">Brand Summary</CardTitle>
        </CardHeader>
        <CardContent>
          {filteredData?.brand_breakdown && filteredData.brand_breakdown.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-2 font-medium">Brand</th>
                    <th className="text-right py-3 px-2 font-medium">Revenue</th>
                    <th className="text-right py-3 px-2 font-medium">COGS</th>
                    <th className="text-right py-3 px-2 font-medium">Shipping</th>
                    <th className="text-right py-3 px-2 font-medium">Ads</th>
                    <th className="text-right py-3 px-2 font-medium">Net Rev</th>
                    <th className="text-right py-3 px-2 font-medium">Margin</th>
                    <th className="text-right py-3 px-2 font-medium">New</th>
                    <th className="text-right py-3 px-2 font-medium">Refill</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.brand_breakdown.map((brand, index) => (
                    <tr key={brand.clinicId} className="border-b border-muted/50 hover:bg-muted/50">
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-2">
                          <span
                            className="w-3 h-3 rounded-full shrink-0"
                            style={{ backgroundColor: brand.color || getBrandColor(brand.clinicId) }}
                          />
                          <span className="font-medium">{brand.displayName}</span>
                        </div>
                      </td>
                      <td className="text-right py-3 px-2">
                        {formatCurrency(brand.summary.total_revenue)}
                      </td>
                      <td className="text-right py-3 px-2 text-muted-foreground">
                        {formatCurrency(brand.expenses?.cogs || 0)}
                      </td>
                      <td className="text-right py-3 px-2 text-muted-foreground">
                        {formatCurrency(brand.expenses?.shipping || 0)}
                      </td>
                      <td className="text-right py-3 px-2 text-muted-foreground">
                        {brand.expenses?.ad_spend ? formatCurrency(brand.expenses.ad_spend) : '-'}
                      </td>
                      <td className={`text-right py-3 px-2 font-medium ${brand.summary.gross_profit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                        {formatCurrency(brand.summary.gross_profit)}
                      </td>
                      <td className="text-right py-3 px-2">
                        {brand.summary.margin_percent}%
                      </td>
                      <td className="text-right py-3 px-2">
                        <span className="font-medium">{formatNumber(brand.summary.new_orders || 0)}</span>
                      </td>
                      <td className="text-right py-3 px-2">
                        <span className="font-medium">{formatNumber(brand.summary.refill_orders || 0)}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-muted/30 font-medium">
                    <td className="py-3 px-2">Total</td>
                    <td className="text-right py-3 px-2">
                      {formatCurrency(filteredData.summary?.total_revenue || 0)}
                    </td>
                    <td className="text-right py-3 px-2 text-muted-foreground">
                      {formatCurrency(filteredData.expenses?.cogs || 0)}
                    </td>
                    <td className="text-right py-3 px-2 text-muted-foreground">
                      {formatCurrency(filteredData.expenses?.shipping || 0)}
                    </td>
                    <td className="text-right py-3 px-2 text-muted-foreground">
                      {filteredData.expenses?.ad_spend ? formatCurrency(filteredData.expenses.ad_spend) : '-'}
                    </td>
                    <td className={`text-right py-3 px-2 ${(filteredData.summary?.gross_profit || 0) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                      {formatCurrency(filteredData.summary?.gross_profit || 0)}
                    </td>
                    <td className="text-right py-3 px-2">
                      {filteredData.summary?.margin_percent || 0}%
                    </td>
                    <td className="text-right py-3 px-2">
                      {formatNumber(filteredData.summary?.new_orders || 0)}
                    </td>
                    <td className="text-right py-3 px-2">
                      {formatNumber(filteredData.summary?.refill_orders || 0)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div className="py-8 text-center text-muted-foreground">
              No data available
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
